import React, {FunctionComponent} from 'react';
import CommandSearchDialog from './ui/dialog';
import CommandSearchTrigger from './ui/trigger';
import {useCommandSearch} from './engine/use-command-search';
import type {UseCommandSearchOptions} from './types';

export interface CommandSearchPaletteProps {
    options: UseCommandSearchOptions;
    triggerPlaceholder?: string;
    dialogPlaceholder?: string;
    emptyHint?: string;
    /** Builds the "no results" message for the current (trimmed) query. */
    noResultsText?: (query: string) => string;
    /** Optional end adornment for the app-bar trigger (e. g. search-mode toggler). */
    triggerEndAdornment?: React.ReactNode;
    triggerFullWidth?: boolean;
    triggerSx?: React.ComponentProps<typeof CommandSearchTrigger>['sx'];
    /** Accessible label for the fullscreen close control (compact viewports). */
    closeLabel?: string;
    /** Accessible label for clearing all recent entries. */
    clearRecentsLabel?: string;
    /** Accessible label for removing one recent entry. */
    removeRecentLabel?: string;
}

/**
 * Reusable command palette: app bar trigger, keyboard shortcut, and categorized search dialog.
 */
const CommandSearchPalette: FunctionComponent<CommandSearchPaletteProps> = ({
    options,
    triggerPlaceholder,
    dialogPlaceholder,
    emptyHint,
    noResultsText,
    triggerEndAdornment,
    triggerFullWidth = false,
    triggerSx,
    closeLabel,
    clearRecentsLabel,
    removeRecentLabel,
}) => {
    const {
        open,
        query,
        setQuery,
        openSearch,
        closeSearch,
        groups,
        allGroups,
        flatResults,
        activeIndex,
        setActiveIndex,
        selectedCategory,
        toggleCategory,
        expandedCategories,
        toggleGroupExpanded,
        selectResult,
        rememberResult,
        removeRecent,
        clearRecents,
        onDialogKeyDown,
        hasQuery,
        isSearching,
    } = useCommandSearch(options);

    return (
        <>
            <CommandSearchTrigger
                onOpen={openSearch}
                placeholder={triggerPlaceholder}
                endAdornment={triggerEndAdornment}
                fullWidth={triggerFullWidth}
                sx={triggerSx}
            />
            <CommandSearchDialog
                open={open}
                query={query}
                onQueryChange={setQuery}
                onClose={closeSearch}
                onKeyDown={onDialogKeyDown}
                groups={groups}
                allGroups={allGroups}
                flatResults={flatResults}
                activeIndex={activeIndex}
                onActiveIndexChange={setActiveIndex}
                onSelect={selectResult}
                onRememberResult={rememberResult}
                selectedCategory={selectedCategory}
                onCategoryToggle={toggleCategory}
                expandedCategories={expandedCategories}
                onGroupExpandToggle={toggleGroupExpanded}
                hasQuery={hasQuery}
                isSearching={isSearching}
                categoryIcons={options.categoryIcons}
                categoryLayouts={options.categoryLayouts}
                searchCategoryLayout={options.searchCategoryLayout}
                placeholder={dialogPlaceholder}
                emptyHint={emptyHint}
                noResultsText={hasQuery ? noResultsText?.(query.trim()) : undefined}
                closeLabel={closeLabel}
                onClearRecents={clearRecents}
                onRemoveRecent={removeRecent}
                clearRecentsLabel={clearRecentsLabel}
                removeRecentLabel={removeRecentLabel}
            />
        </>
    );
};

export default CommandSearchPalette;

export {default as CommandSearchDialog} from './ui/dialog';
export {default as CommandSearchTrigger} from './ui/trigger';

export * from './types';
export * from './scope/types';
export * from './scope/keys';
export * from './scope/resolve-route';
export * from './match-query';
export * from './cache/command-search-cache';
export * from './recent/recent-store';
export * from './recent/recent-handlers';
export * from './registry/registry';
export * from './registry/context';
export * from './registry/use-command-search-scope';
export * from './engine/use-command-search';
export * from './engine/use-command-shortcut';
export * from './engine/use-has-keyboard-input';
export * from './category-layout';
export * from './engine/merge-results';
export * from './navigation/create-navigation-provider';
export * from './navigation/resolve-navigation-item';
